import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Section, Eyebrow } from "@/components/Bits";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { ARTICLES } from "@/data/site";

export default function Guides() {
  const featured = ARTICLES.find((a) => a.featured) || ARTICLES[0];
  const rest = ARTICLES.filter((a) => a.slug !== featured.slug);

  return (
    <>
      <Seo title="Home Education Ideas & Guides | FreeRange.Education" description="Practical thoughts, useful guides and ideas drawn from real experience of home education and the products we build." />

      <Section className="pb-16 pt-32 md:pt-40">
        <Reveal>
          <Eyebrow color="text-turquoise">Ideas & Guides</Eyebrow>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-charcoal sm:text-6xl lg:text-7xl">
            Ideas & Guides
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-charcoal/75">
            Practical thoughts, useful guides and ideas drawn from real experience of home education and the products
            we build.
          </p>
          <p className="mt-3 text-sm italic text-charcoal/50">
            The pieces below are example / future content to show the layout — more will follow.
          </p>
        </Reveal>
      </Section>

      {/* Featured */}
      <Section className="pb-16">
        <Reveal>
          <Link
            to={`/guides/${featured.slug}`}
            className="group grid overflow-hidden rounded-[2rem] bg-purple text-cream lg:grid-cols-2"
            data-testid="guides-featured"
          >
            <div className="overflow-hidden">
              <ImagePlaceholder src={featured.image} alt={featured.title} ratio="wide" rounded="rounded-none" testid="guides-featured-img" className="h-full" />
            </div>
            <div className="flex flex-col justify-center p-10 md:p-14">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-turquoise">Featured · {featured.category}</span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">{featured.title}</h2>
              <p className="mt-4 text-lg leading-relaxed text-cream/80">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-semibold text-turquoise">
                Read article <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>
      </Section>

      {/* Grid */}
      <Section className="pb-24 md:pb-32">
        <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((a) => (
            <StaggerItem key={a.slug}>
              <Link
                to={`/guides/${a.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-charcoal/10 bg-white transition-transform duration-300 hover:-translate-y-1.5"
                data-testid={`guide-card-${a.slug}`}
              >
                <ImagePlaceholder src={a.image} alt={a.title} ratio="wide" rounded="rounded-none" testid={`guide-img-${a.slug}`} />
                <div className="flex flex-1 flex-col p-7">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-marigold">{a.category}</span>
                  <h3 className="mt-2 font-display text-2xl font-bold leading-snug tracking-tight text-charcoal">{a.title}</h3>
                  <p className="mt-2 flex-1 leading-relaxed text-charcoal/70">{a.excerpt}</p>
                  <span className="mt-5 text-sm text-charcoal/50">{a.readTime}</span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>
    </>
  );
}
